/**
 * @license
 * Copyright Tyler Technologies, Inc. 
 * License: Apache-2.0
 */
import { getShadowElement } from '@tylertech/forge-core';
import { replaceElement } from '../../core/utils/utils';
import { BaseAdapter } from '../../core/base/base-adapter';
import { APP_BAR_CONSTANTS } from './app-bar-constants';
import { STATE_LAYER_CONSTANTS } from '../../state-layer';
import { FOCUS_INDICATOR_CONSTANTS } from '../../focus-indicator';
export class AppBarAdapter extends BaseAdapter {
    constructor(component) {
        super(component);
        this._rootElement = getShadowElement(component, APP_BAR_CONSTANTS.selectors.ROOT);
        this._logoTitleContainerElement = getShadowElement(component, APP_BAR_CONSTANTS.selectors.LOGO_TITLE_CONTAINER);
        this._centerSectionElement = getShadowElement(component, APP_BAR_CONSTANTS.selectors.CENTER_SECTION);
        this._centerSlotElement = getShadowElement(component, APP_BAR_CONSTANTS.selectors.CENTER_SLOT);
    }
    initialize() {
        if (!this._component.hasAttribute('role')) {
            this._component.setAttribute('role', 'banner');
        }
    }
    setTitleText(value) {
        if (value?.trim().length) {
            if (!this._titleElement) {
                this._titleElement = this._createTitleElement();
                const titleSlot = this._logoTitleContainerElement.querySelector('slot[name=title]');
                titleSlot?.append(this._titleElement);
            }
            this._titleElement.textContent = value;
        }
        else {
            this._titleElement?.remove();
            this._titleElement = undefined;
        }
    }
    setHref(value, listener) {
        const isAnchor = this._logoTitleContainerElement.tagName === 'A';
        if (value) {
            if (!isAnchor) {
                this._createAnchorElement();
            }
            this._logoTitleContainerElement.href = value;
            this._logoTitleContainerElement.addEventListener('click', listener, { capture: true });
        }
        else if (isAnchor) {
            this._logoTitleContainerElement.removeEventListener('click', listener, { capture: true });
            this._logoTitleContainerElement = replaceElement(this._logoTitleContainerElement, document.createElement('div'));
            this._logoTitleContainerElement.classList.add(APP_BAR_CONSTANTS.classes.LOGO_TITLE_CONTAINER);
            this._logoTitleContainerElement.querySelector(STATE_LAYER_CONSTANTS.elementName)?.remove();
            this._logoTitleContainerElement.querySelector(FOCUS_INDICATOR_CONSTANTS.elementName)?.remove();
        }
    }
    setTarget(value) {
        if (this._logoTitleContainerElement.tagName === 'A') {
            this._logoTitleContainerElement.target = value;
        }
    }
    addCenterSlotListener(listener) {
        this._centerSlotElement.addEventListener('slotchange', listener);
    }
    setCenterSlotVisibility() {
        if (this._centerSlotElement.assignedNodes().length) {
            this._centerSectionElement.style.removeProperty('display');
            this._rootElement.classList.remove(APP_BAR_CONSTANTS.classes.NO_CENTER);
        }
        else {
            this._centerSectionElement.style.display = 'none';
            this._rootElement.classList.add(APP_BAR_CONSTANTS.classes.NO_CENTER);
        }
    }
    _createAnchorElement() {
        this._logoTitleContainerElement = replaceElement(this._logoTitleContainerElement, document.createElement('a'));
        this._logoTitleContainerElement.classList.add(APP_BAR_CONSTANTS.classes.LOGO_TITLE_CONTAINER);
        const stateLayer = document.createElement('forge-state-layer');
        const focusIndicator = document.createElement('forge-focus-indicator');
        focusIndicator.inward = true;
        this._logoTitleContainerElement.append(stateLayer, focusIndicator);
    }
    _createTitleElement() {
        const el = document.createElement('h1');
        el.classList.add(APP_BAR_CONSTANTS.classes.TITLE);
        el.setAttribute('part', 'title');
        return el;
    }
}
